import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import {
  Button,
  Snackbar,
  Alert,
  Slide,
  Grid,
  Avatar,
  Typography,
  Box,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  CircularProgress,
  useTheme,
  useMediaQuery,
  Select,
  MenuItem,
  FormControl,
  Tooltip,
  Switch,
  Collapse,
  Tabs,
  Tab,
  TextField,
  Divider,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  ListItemSecondaryAction,
  Paper,
  Slider,
  InputLabel,
} from "@mui/material";
import {
  ContentCopy,
  CalendarMonth,
  Link,
  CheckCircle,
  Info,
  Google,
  CalendarToday,
  Schedule,
  ContactMail,
  PlayCircleOutline,
  ExpandMore,
  ExpandLess,
  InfoOutlined,
  CreditCardOff,
  Groups,
  PersonAdd,
  Close,
  Add,
  Delete,
  Edit,
  Person,
  PriorityHigh,
  Settings,
  AccessTime,
} from "@mui/icons-material";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import AnseraLogo from "assets/images/Anser.png";
import axios from "axios";
import { useAuth } from "context/AuthContext";
import { FAST_API_BASE_URL, LAMBDA_BASE_URL } from "constants";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

const getLoomEmbedUrl = (shareUrl) => {
  if (!shareUrl) return null;
  return shareUrl.replace('/share/', '/embed/');
};

const LOOM_SHARE_URLS = {
  'google-calendar': process.env.REACT_APP_GOOGLE_CALENDAR_LOOM_URL,
  'calendly': process.env.REACT_APP_CALENDLY_LOOM_URL,
  'zoho-calendar': process.env.REACT_APP_ZOHO_CALENDAR_LOOM_URL,
  'team-scheduling': process.env.REACT_APP_TEAM_SCHEDULING_LOOM_URL,
};

const DEFAULT_PROVIDER = "google-calendar";
const TEAM_SCHEDULING_PROVIDER = "team-scheduling";

const calendarProviders = [
  {
    id: "google-calendar",
    name: "Google Calendar",
    logo: "https://upload.wikimedia.org/wikipedia/commons/a/a5/Google_Calendar_icon_%282020%29.svg",
    description: "Sync calendar events and schedule meetings seamlessly",
    color: "#219297",
    icon: <Google />,
    hasVideo: true,
    type: "single-user",
  },
  {
    id: "calendly",
    name: "Calendly",
    logo: "https://www.bing.com/th/id/OIP.isWc_EiA6Ms4U-FNJlRwEwHaHa?w=193&h=211&c=8&rs=1&qlt=90&o=6&cb=ucfimg1&pid=3.1&rm=2&ucfimg=1",
    description: "Professional scheduling and meeting management",
    color: "#00A4FF",
    icon: <Schedule />,
    hasVideo: true,
    type: "single-user",
  },
  {
    id: "zoho-calendar",
    name: "Zoho Calendar",
    logo: "https://www.zoho.com/branding/images/zoho-logo.png",
    description: "Appointment scheduling with Zoho integration",
    color: "#E42526",
    icon: <CalendarToday />,
    hasVideo: true,
    type: "single-user",
  },
  {
    id: "contact-form",
    name: "Contact Form",
    logo: "",
    description: "Your contact page or form URL for inquiries",
    color: "#6B46C1",
    icon: <ContactMail />,
    hasVideo: false,
    type: "single-user",
  },
  {
    id: TEAM_SCHEDULING_PROVIDER,
    name: "Team Scheduling",
    logo: "",
    description: "Round-robin scheduling with multiple team members",
    color: "#FF6900",
    icon: <Groups />,
    hasVideo: true,
    type: "team",
  },
];

const providerInstructions = {
  "google-calendar": [
    { title: "Sign in to Google Calendar", content: "Use a Google Workspace account to access appointment scheduling features." },
    { title: "Create Appointment Schedule", content: "On the left panel, click 'Create' → 'Appointment schedule'." },
    { title: "Configure Settings", content: "Set title, availability, duration, and meeting location preferences." },
    { title: "Save & Share", content: "Save the schedule, then click 'Share' → 'Copy booking page link'." },
    { title: "Paste URL", content: "The copied link is your public appointment booking URL." },
  ],
  "calendly": [
    { title: "Sign in to Calendly", content: "Access your Calendly dashboard." },
    { title: "Create Event Type", content: "Create a new event type (15 min, 30 min, custom, etc.)." },
    { title: "Configure Settings", content: "Set availability, location, and form questions." },
    { title: "Publish & Share", content: "Publish the event type and copy the 'Share your link' URL." },
    { title: "Paste URL", content: "Use the copied Calendly link as your booking URL." },
  ],
  "zoho-calendar": [
    { title: "Sign in to Zoho Calendar", content: "Access your Zoho Calendar dashboard." },
    { title: "Navigate to Settings", content: "Right-click the calendar from the left pane and click Edit, or go to Settings > Calendar > My Calendars" },
    { title: "Enable public access", content: "Under Sharing and permissions > Public access, enable public access for the calendar." },
    { title: "Configure permissions", content: "Choose if you want others to View only free/busy status or View event details." },
    { title: "Customize interface", content: "Click Customize near Schedule Appointment to customize your interface." },
    { title: "Choose language and color", content: "Choose desired language and color for your interface." },
    { title: "Copy the iframe source URL", content: "Copy the src URL from the iframe under Schedule Appointment section." },
    { title: "Paste the URL here", content: "Paste the copied URL in the field above." },
  ],
  "contact-form": [
    { title: "Ensure Accessibility", content: "Make sure the page is publicly accessible and not behind a login." },
    { title: "Form Functionality", content: "Ensure the form is working properly and can receive messages." },
    { title: "Paste URL", content: "Paste your contact page URL in the field on the left." },
  ],
  "team-scheduling": [
    { title: "Connect Google Calendar", content: "Enable Google Calendar integration first to access team scheduling." },
    { title: "Configure Team Members", content: "Add team members who have shared their calendars with you." },
    { title: "Set Scheduling Rules", content: "Configure working hours, slot duration, and booking rules." },
  //   { title: "Share Booking Page", content: "Share the public booking link with your clients." },
  //   { title: "Manage Appointments", content: "View and manage all team appointments from the dashboard." },
  ],
};

const providerExamples = {
  "google-calendar": "https://calendar.google.com/calendar/appointments/schedules/abc123xyz456\nhttps://calendar.app.google.com/calendar/schedule",
  "calendly": "https://calendly.com/yourname/event\nhttps://calendly.com/yourname/30min",
  "zoho-calendar": "https://calendar.zoho.com/book/yourname\nhttps://calendar.zoho.com/meeting/abc123",
  "contact-form": "https://yourwebsite.com/contact\nhttps://yourwebsite.com/contact-form",
  "team-scheduling": "https://yourdomain.com/api/public/booking/availability\n(Generated automatically)",
};

const providerPricing = {
  "google-calendar": "Free",
  "calendly": "Free",
  "zoho-calendar": "Included in Zoho Workplace paid plans",
  "contact-form": "Free (Your own website)",
  "team-scheduling": "Free",
};

const providerPlaceholders = {
  "google-calendar": "Enter your Google Calendar appointment schedule URL",
  "calendly": "Enter your Calendly event URL",
  "zoho-calendar": "Enter your Zoho Calendar booking URL",
  "contact-form": "Enter your contact page/form URL",
  "team-scheduling": "Team scheduling uses connected Google Calendar",
};

const providerLabels = {
  "google-calendar": "Google Calendar Booking URL",
  "calendly": "Calendly Event URL",
  "zoho-calendar": "Zoho Calendar Booking URL",
  "contact-form": "Contact Page/Form URL",
  "team-scheduling": "Team Scheduling Configuration",
};

const CustomInput = React.memo(({ 
  value, 
  onChange, 
  placeholder, 
  error, 
  helperText, 
  disabled, 
  startIcon, 
  endAdornment,
  type = "text",
  onKeyPress,
  className = "",
  fullWidth = true,
  style = {}
}) => {
  const inputRef = useRef(null);

  const handleFocus = () => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  return (
    <Box 
      sx={{ 
        position: 'relative',
        width: fullWidth ? '100%' : 'auto',
        ...style
      }}
      className={className}
    >
      <Box
        onClick={handleFocus}
        sx={{
          position: 'relative',
          cursor: disabled ? 'not-allowed' : 'text',
          backgroundColor: disabled ? '#f5f5f5' : '#ffffff',
          border: `1px solid ${error ? '#d32f2f' : '#e0e0e0'}`,
          borderRadius: '8px',
          padding: '12px 16px',
          transition: 'all 0.2s ease',
          '&:hover': !disabled && {
            borderColor: error ? '#d32f2f' : '#219297',
            boxShadow: '0 2px 8px rgba(33, 146, 151, 0.1)',
          },
          '&:focus-within': {
            borderColor: error ? '#d32f2f' : '#219297',
            boxShadow: '0 0 0 3px rgba(33, 146, 151, 0.1)',
            outline: 'none',
          },
        }}
      >
        {startIcon && (
          <Box 
            sx={{ 
              position: 'absolute', 
              left: '16px', 
              top: '50%', 
              transform: 'translateY(-50%)',
              color: error ? '#d32f2f' : '#666',
              display: 'flex',
              alignItems: 'center',
              zIndex: 1
            }}
          >
            {startIcon}
          </Box>
        )}
        
        <input
          ref={inputRef}
          type={type}
          value={value}
          onChange={onChange}
          onKeyPress={onKeyPress}
          placeholder={placeholder}
          disabled={disabled}
          style={{
            width: '100%',
            border: 'none',
            background: 'transparent',
            fontSize: '0.95rem',
            color: '#333',
            outline: 'none',
            padding: startIcon ? '0 0 0 40px' : '0',
            fontFamily: 'inherit',
            lineHeight: '1.5',
            cursor: disabled ? 'not-allowed' : 'text',
            opacity: disabled ? 0.7 : 1,
          }}
        />
        
        {endAdornment && (
          <Box 
            sx={{ 
              position: 'absolute', 
              right: '8px', 
              top: '50%', 
              transform: 'translateY(-50%)',
              display: 'flex',
              alignItems: 'center',
              gap: '4px'
            }}
          >
            {endAdornment}
          </Box>
        )}
      </Box>
      
      {helperText && (
        <Typography
          sx={{
            fontSize: '0.75rem',
            color: error ? '#d32f2f' : '#666',
            marginTop: '4px',
            marginLeft: '4px',
            lineHeight: 1.4,
          }}
        >
          {helperText}
        </Typography>
      )}
    </Box>
  );
});

const InstructionStep = React.memo(({ number, title, children, icon }) => (
  <Box sx={{ 
    mb: 2,
    display: 'flex',
    gap: 2,
    alignItems: 'flex-start'
  }}>
    <Box
      sx={{
        width: 28,
        height: 28,
        minWidth: 28,
        borderRadius: '50%',
        backgroundColor: '#e0f7fa',
        color: '#219297',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontWeight: 'bold',
        fontSize: '0.85rem',
        flexShrink: 0,
      }}
    >
      {icon || number}
    </Box>
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography 
        variant="subtitle2"
        fontWeight="600" 
        sx={{ 
          color: '#333',
          mb: 0.5,
          fontSize: '0.9rem'
        }}
      >
        {title}
      </Typography>
      <Typography
        sx={{
          color: '#666',
          fontSize: '0.85rem',
          lineHeight: 1.5,
        }}
      >
        {children}
      </Typography>
    </Box>
  </Box>
));

const TeamManagementModal = React.memo(({ open, onClose, domainId, refreshData, initialTeamData }) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [teamMembers, setTeamMembers] = useState(initialTeamData || []);
  const [sharedCalendars, setSharedCalendars] = useState([]);
  const [config, setConfig] = useState(null);
  const [newConfig, setNewConfig] = useState({
    slot_duration_minutes: 30,
    buffer_before_minutes: 5,
    buffer_after_minutes: 5,
    working_hours_start: '09:00',
    working_hours_end: '17:00',
    working_days: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'],
    min_lead_time_hours: 2,
    max_booking_days: 60,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC",
  });
  const [activeTab, setActiveTab] = useState('team-members');
  const [newMemberPriority, setNewMemberPriority] = useState(5);
  const [selectedCalendar, setSelectedCalendar] = useState('');
  const [editingPriorityFor, setEditingPriorityFor] = useState(null);
  const [editingPriorityValue, setEditingPriorityValue] = useState(5);

 useEffect(() => {
    if (!open || !domainId) return;
    
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      
      try {
        // Fetch shared calendars
        const calendarsRes = await axios.get(`${FAST_API_BASE_URL}/api/team-scheduling/writable-calendars`, {
          headers: { "DOMAIN-TOKEN": domainId }
        }).catch(() => ({ data: { success: false, data: { calendars: [] } } }));

        if (calendarsRes.data.success) {
          const calendars = calendarsRes.data.data.calendars || [];
          const processedCalendars = calendars.map(calendar => ({
            ...calendar,
            id: calendar.id || calendar.email,
            name: calendar.name || calendar.summary || calendar.email
          }));
          setSharedCalendars(processedCalendars);
        }

        // Fetch team scheduling status to get config
        const statusRes = await axios.get(`${FAST_API_BASE_URL}/api/team-scheduling/status`, {
          headers: { "DOMAIN-TOKEN": domainId }
        }).catch(() => ({ data: { success: false, data: {} } }));

        if (statusRes.data.success) {
          const statusData = statusRes.data.data;
          
          // Update team members from status or use initialTeamData
          if (statusData.team_members && statusData.team_members.length > 0) {
            setTeamMembers(statusData.team_members);
          } else if (initialTeamData) {
            setTeamMembers(initialTeamData);
          }
          
          // Update configuration from status
          if (statusData.config) {
            const configData = statusData.config;
            setConfig(configData);
            setNewConfig({
              slot_duration_minutes: configData.slot_duration_minutes || 30,
              buffer_before_minutes: configData.buffer_before_minutes || 5,
              buffer_after_minutes: configData.buffer_after_minutes || 5,
              working_hours_start: configData.working_hours_start || '09:00',
              working_hours_end: configData.working_hours_end || '17:00',
              working_days: configData.working_days || ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'],
              min_lead_time_hours: configData.min_lead_time_hours || 2,
              max_booking_days: configData.max_booking_days || 60,
              timezone: configData.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC",
            });
          }
        }

      } catch (err) {
        console.error("Failed to fetch team data:", err);
        setError("Failed to load team data. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [open, domainId, initialTeamData]);

  const handleAddTeamMember = async () => {
    if (!selectedCalendar) {
      setError("Please select a calendar first");
      return;
    }
    
    const calendar = availableCalendars.find(c => 
      c.id === selectedCalendar || 
      c.email === selectedCalendar ||
      String(c.id) === String(selectedCalendar)
    );
    
    if (!calendar) {
      setError("Selected calendar not found. Please select again.");
      return;
    }
    
    try {
      setSaving(true);
      const res = await axios.post(
        `${FAST_API_BASE_URL}/api/team-scheduling/configure-member`,
        {
          calendar_id: calendar.id || calendar.email,
          priority: newMemberPriority,
          name: calendar.name || calendar.summary || calendar.email || calendar.id,
          enabled: true,
        },
        { headers: { "DOMAIN-TOKEN": domainId } }
      );
      
      if (res.data.success) {
        const storageMethod = res.data.data?.storage_method || 'Google Calendar event';
        setSuccess(`Added team member successfully (stored in ${storageMethod})`);
        

        const newMember = {
          calendar_id: calendar.id || calendar.email,
          priority: newMemberPriority,
          name: calendar.name || calendar.summary || calendar.email || calendar.id,
          email: calendar.email,
          enabled: true
        };
        
        setTeamMembers(prev => [...prev, newMember]);
        setSelectedCalendar('');
        
        setTimeout(() => setSuccess(null), 3000);
      } else {
        setError(res.data.message || "Failed to add team member");
      }
    } catch (err) {
      console.error("Failed to add team member:", err);
      const errorMsg = err.response?.data?.message || "Failed to add team member. Please try again.";
      
      if (errorMsg.includes('No write access') || errorMsg.includes('writer') || errorMsg.includes('owner')) {
        setError(`Cannot add ${calendar.name || calendar.email}: You need "writer" or "owner" access to this calendar.`);
      } else {
        setError(errorMsg);
      }
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveTeamMember = async (calendarId) => {
    if (!calendarId) return;
    
    try {
      setSaving(true);
      const res = await axios.post(
        `${FAST_API_BASE_URL}/api/team-scheduling/configure-member`,
        {
          calendar_id: calendarId,
          enabled: false,
        },
        { headers: { "DOMAIN-TOKEN": domainId } }
      );
      
      if (res.data.success) {
        setTeamMembers(prev => prev.filter(member => member.calendar_id !== calendarId));
        setSuccess("Team member removed");
        setTimeout(() => setSuccess(null), 3000);
      } else {
        setError("Failed to remove team member");
      }
    } catch (err) {
      console.error("Failed to remove team member:", err);
      setError("Failed to remove team member. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const handleUpdatePriority = async (calendarId, priority) => {
    try {
      setSaving(true);
      const res = await axios.post(
        `${FAST_API_BASE_URL}/api/team-scheduling/configure-member`,
        {
          calendar_id: calendarId,
          priority: priority,
        },
        { headers: { "DOMAIN-TOKEN": domainId } }
      );
      
      if (res.data.success) {
        setTeamMembers(prev => prev.map(member => 
          member.calendar_id === calendarId ? { ...member, priority } : member
        ));
        setEditingPriorityFor(null);
      } else {
        setError("Failed to update priority");
      }
    } catch (err) {
      console.error("Failed to update priority:", err);
      setError("Failed to update priority. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const handleSaveConfig = async () => {
    try {
      setSaving(true);
      const res = await axios.post(
        `${FAST_API_BASE_URL}/api/team-scheduling/config`,
        { config: newConfig },
        { headers: { "DOMAIN-TOKEN": domainId } }
      );
      
      if (res.data.success) {
        setConfig(newConfig);
        setSuccess("Configuration saved successfully");
        setTimeout(() => setSuccess(null), 3000);
      } else {
        setError("Failed to save configuration");
      }
    } catch (err) {
      console.error("Failed to save config:", err);
      setError("Failed to save configuration. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const availableCalendars = useMemo(() => {
    return sharedCalendars.filter(calendar => {
      const isAlreadyMember = teamMembers.some(member => {
        const memberIdentifiers = [member.calendar_id, member.email, member.id].filter(Boolean);
        const calendarIdentifiers = [calendar.id, calendar.email, calendar.summary].filter(Boolean);
        return memberIdentifiers.some(memberId => 
          calendarIdentifiers.some(calendarId => 
            memberId && calendarId && memberId.toLowerCase() === calendarId.toLowerCase()
          )
        );
      });
      return !isAlreadyMember;
    });
  }, [sharedCalendars, teamMembers]);

  if (loading) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Typography variant="h6">Team Scheduling Management</Typography>
            <IconButton onClick={onClose} size="small">
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box display="flex" justifyContent="center" alignItems="center" py={4}>
            <CircularProgress color="teal" />
            <Typography variant="body2" sx={{ ml: 2 }}>
              Loading team data...
            </Typography>
          </Box>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography variant="h6">Team Scheduling Management</Typography>
          <IconButton onClick={onClose} size="small">
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>
      
      <DialogContent>
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
          <Box sx={{ display: 'flex' }}>
            <Button
              variant={activeTab === 'team-members' ? 'contained' : 'text'}
              onClick={() => setActiveTab('team-members')}
              sx={{ 
                mr: 1,
                borderRadius: '8px 8px 0 0',
                backgroundColor: activeTab === 'team-members' ? '#219297' : 'transparent',
                color: activeTab === 'team-members' ? '#fff' : '#666',
              }}
              startIcon={<Groups />}
            >
              Team Members
            </Button>
            <Button
              variant={activeTab === 'settings' ? 'contained' : 'text'}
              onClick={() => setActiveTab('settings')}
              sx={{ 
                borderRadius: '8px 8px 0 0',
                backgroundColor: activeTab === 'settings' ? '#219297' : 'transparent',
                color: activeTab === 'settings' ? '#fff' : '#666',
              }}
              startIcon={<Settings />}
            >
              Settings
            </Button>
          </Box>
        </Box>

        {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {activeTab === 'team-members' && (
          <Box>
            <Typography variant="subtitle1" fontWeight="600" sx={{ mb: 2, color: '#219297' }}>
              Current Team Members ({teamMembers.length})
            </Typography>
            
            {teamMembers.length > 0 ? (
              <List>
                {teamMembers.map((member, index) => (
                  <ListItem 
                    key={index}
                    sx={{ 
                      backgroundColor: '#f8fafa',
                      mb: 1,
                      borderRadius: '8px',
                      border: '1px solid #e0f0f0'
                    }}
                  >
                    <ListItemAvatar>
                      <Avatar sx={{ bgcolor: '#219297' }}>
                        {member.name?.[0]?.toUpperCase() || 'T'}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Box display="flex" alignItems="center" gap={1}>
                          <Typography variant="body1" fontWeight="500">
                            {member.name || member.email || member.calendar_id}
                          </Typography>
                          {editingPriorityFor === member.calendar_id ? (
                            <Box display="flex" alignItems="center" gap={1}>
                              <Slider
                                value={editingPriorityValue}
                                onChange={(e, value) => setEditingPriorityValue(value)}
                                min={1}
                                max={10}
                                step={1}
                                sx={{ width: 100, color: '#219297' }}
                              />
                              <Button
                                size="small"
                                variant="contained"
                                onClick={() => handleUpdatePriority(member.calendar_id || member.email, editingPriorityValue)}
                                disabled={saving}
                                sx={{ minWidth: 'auto', padding: '4px 8px' }}
                              >
                                Save
                              </Button>
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={() => setEditingPriorityFor(null)}
                                sx={{ minWidth: 'auto', padding: '4px 8px' }}
                              >
                                Cancel
                              </Button>
                            </Box>
                          ) : (
                            <Chip 
                              label={`Priority: ${member.priority}`}
                              size="small"
                              sx={{ 
                                backgroundColor: '#e0f7fa',
                                color: '#219297',
                                fontSize: '0.7rem'
                              }}
                            />
                          )}
                        </Box>
                      }
                      secondary={member.email || member.calendar_id}
                    />
                    <ListItemSecondaryAction>
                      <Box display="flex" gap={1}>
                        <IconButton 
                          size="small"
                          onClick={() => {
                            setEditingPriorityFor(member.calendar_id || member.email);
                            setEditingPriorityValue(member.priority || 5);
                          }}
                        >
                          <Edit fontSize="small" />
                        </IconButton>
                        <IconButton 
                          size="small"
                          color="error"
                          onClick={() => {
                            if (window.confirm(`Remove ${member.name || member.email || member.calendar_id} from team?`)) {
                              handleRemoveTeamMember(member.calendar_id || member.email);
                            }
                          }}
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </Box>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
            ) : (
              <Alert severity="info" sx={{ mb: 2 }}>
                No team members configured yet. Add team members below.
              </Alert>
            )}

            <Divider sx={{ my: 3 }} />

            <Typography variant="subtitle1" fontWeight="600" sx={{ mb: 2, color: '#219297' }}>
              Add Team Member
            </Typography>
            
            {availableCalendars.length > 0 ? (
              <Paper sx={{ p: 2, backgroundColor: '#f8fafa' }}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                      {/* <InputLabel id="calendar-select-label" sx={{color:"#219297"}}>Select Calendar</InputLabel> */}
                      <Select
                        labelId="calendar-select-label"
                        value={selectedCalendar}
                        onChange={(e) => setSelectedCalendar(e.target.value)}
            
                        MenuProps={{
                          PaperProps: {
                            sx: {
                              maxHeight: 300,
                              zIndex: 1300,
                            },
                          },
                        }}
                        displayEmpty
                        renderValue={(selected) => {
                          if (!selected) {
                            return <em style={{ color: '#999' }}>Select a calendar...</em>;
                          }
                          
                          const selectedCalendarObj = availableCalendars.find(c => 
                            c.id === selected || 
                            c.email === selected || 
                            String(c.id) === String(selected)
                          );
                          
                          if (!selectedCalendarObj) {
                            return <em style={{ color: '#999' }}>Select a calendar...</em>;
                          }
                          
                          const displayName = selectedCalendarObj?.name || 
                                            selectedCalendarObj?.summary || 
                                            selectedCalendarObj?.email || 
                                            selectedCalendarObj?.id;
                          
                          return displayName;
                        }}
                      >
                        <MenuItem value="">
                          <em>Select a calendar...</em>
                        </MenuItem>
                        {availableCalendars.map((calendar) => {
                          const calendarValue = calendar.id || calendar.email;
                          
                          if (!calendarValue) {
                            console.warn("Calendar missing ID and email:", calendar);
                            return null;
                          }
                          
                          const displayName = calendar?.name || 
                                            calendar?.summary || 
                                            calendar?.email || 
                                            calendar?.id;
                          const displayEmail = calendar?.email || calendar?.id;
                          
                          return (
                            <MenuItem key={calendarValue} value={calendarValue}>
                              <Box display="flex" alignItems="center" gap={1}>
                                <CalendarToday sx={{ color: '#219297' }} />
                                <Box>
                                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                    {displayName}
                                  </Typography>
                                  {displayEmail && displayEmail !== displayName && (
                                    <Typography variant="caption" color="textSecondary">
                                      {displayEmail}
                                    </Typography>
                                  )}
                                </Box>
                              </Box>
                            </MenuItem>
                          );
                        }).filter(Boolean)}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Box>
                      <Typography variant="caption" display="block" gutterBottom>
                        Priority (1-10, lower = higher priority)
                      </Typography>
                      <Slider
                        value={newMemberPriority}
                        onChange={(e, value) => setNewMemberPriority(value)}
                        min={1}
                        max={10}
                        step={1}
                        marks
                        valueLabelDisplay="auto"
                        sx={{ color: '#219297' }}
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <Button
                      variant="contained"
                      fullWidth
                      onClick={handleAddTeamMember}
                      disabled={!selectedCalendar || saving}
                      startIcon={<Add />}
                      sx={{ height: '30px',backgroundColor: '#219297',color: '#ffffff',":hover":{backgroundColor: '#197f85'}}}
                    >
                      {saving ? <CircularProgress size={20} /> : 'Add'}
                    </Button>
                  </Grid>
                </Grid>
              </Paper>
            ) : (
              <Alert severity="info">
                No additional calendars available to add. Team members must share their calendars with your account.
              </Alert>
            )}
          </Box>
        )}

        {activeTab === 'settings' && (
          <Box>
            <Typography variant="subtitle1" fontWeight="600" sx={{ mb: 3, color: '#219297' }}>
              Scheduling Configuration
            </Typography>

            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" sx={{ mb: 1, color: '#666' }}>
                  Slot Duration (minutes)
                </Typography>
                <CustomInput
                  type="number"
                  value={newConfig.slot_duration_minutes}
                  onChange={(e) => setNewConfig({
                    ...newConfig,
                    slot_duration_minutes: parseInt(e.target.value) || 30
                  })}
                  placeholder="Slot Duration (minutes)"
                  helperText="Duration of each booking slot"
                  startIcon={<AccessTime />}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography variant="body2" sx={{ mb: 1, color: '#666' }}>
                  Working Hours
                </Typography>
                <Box display="flex" alignItems="center" gap={1}>
                  <CustomInput
                    type="time"
                    value={newConfig.working_hours_start}
                    onChange={(e) => setNewConfig({
                      ...newConfig,
                      working_hours_start: e.target.value
                    })}
                    className="working-hours-input"
                    fullWidth={false}
                    style={{ flex: 1 }}
                  />
                  <Typography variant="body2" sx={{ mx: 1 }}>to</Typography>
                  <CustomInput
                    type="time"
                    value={newConfig.working_hours_end}
                    onChange={(e) => setNewConfig({
                      ...newConfig,
                      working_hours_end: e.target.value
                    })}
                    className="working-hours-input"
                    fullWidth={false}
                    style={{ flex: 1 }}
                  />
                </Box>
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography variant="body2" sx={{ mb: 1, color: '#666' }}>
                  Maximum Booking Days
                </Typography>
                <CustomInput
                  type="number"
                  value={newConfig.max_booking_days}
                  onChange={(e) => setNewConfig({
                    ...newConfig,
                    max_booking_days: parseInt(e.target.value) || 90
                  })}
                  placeholder="Maximum Booking Days"
                  helperText="How far ahead to book"
                  startIcon={<CalendarMonth />}
                />
              </Grid>

              <Grid item xs={12}>
                <Typography variant="body2" sx={{ mb: 1, color: '#666' }}>
                  Working Days
                </Typography>
                <Box display="flex" gap={1} flexWrap="wrap">
                  {['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'].map((day) => (
                    <Chip
                      key={day}
                      label={day.charAt(0).toUpperCase() + day.slice(1)}
                      onClick={() => {
                        const days = [...newConfig.working_days];
                        if (days.includes(day)) {
                          setNewConfig({
                            ...newConfig,
                            working_days: days.filter(d => d !== day)
                          });
                        } else {
                          setNewConfig({
                            ...newConfig,
                            working_days: [...days, day]
                          });
                        }
                      }}
                      color={newConfig.working_days.includes(day) ? 'primary' : 'default'}
                      sx={{
                        backgroundColor: newConfig.working_days.includes(day) ? '#219297' : '#e0e0e0',
                        color: newConfig.working_days.includes(day) ? '#fff' : '#666',
                        ":hover":{ backgroundColor: newConfig.working_days.includes(day) ? '#197f85' : '#d5d5d5'}
                      }}
                    />
                  ))}
                </Box>
              </Grid>
            </Grid>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} sx={{ color: '#666' }}>
          Cancel
        </Button>
        {activeTab === 'settings' && (
          <Button
            variant="contained"
            onClick={handleSaveConfig}
            disabled={saving}
            sx={{ backgroundColor: '#219297',color: '#ffffff' }}
          >
            {saving ? <CircularProgress size={20} /> : 'Save Configuration'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
});

const useGoogleCalendarStatus = (domainId) => {
  const [hasGoogleCredentials, setHasGoogleCredentials] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const checkGoogleCredentials = useCallback(async () => {
    if (!domainId) return false;
    
    try {
      setIsLoading(true);
      const res = await axios.get(`${FAST_API_BASE_URL}/api/mcp-credentials/google-calendar`, {
        headers: { "DOMAIN-TOKEN": domainId },
      }).catch(() => ({ data: { result: null } }));
      
      const credentials = res?.data?.result;
      const hasValidCredentials = credentials?.client_id && 
                                 credentials?.client_secret && 
                                 credentials.client_id.trim() !== '' && 
                                 credentials.client_secret.trim() !== '';
      
      setHasGoogleCredentials(hasValidCredentials);
      return hasValidCredentials;
    } catch (err) {
      console.error("Failed to check Google credentials:", err);
      setHasGoogleCredentials(false);
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [domainId]);

  return {
    hasGoogleCredentials,
    isLoading,
    checkGoogleCredentials,
    setHasGoogleCredentials
  };
};

const useSingleProvider = (domainId) => {
  const [selectedProvider, setSelectedProvider] = useState(DEFAULT_PROVIDER);
  const [calendarUrl, setCalendarUrl] = useState("");
  const [hasExistingConnection, setHasExistingConnection] = useState(false);
  const [existingProvider, setExistingProvider] = useState("");
  const [existingUrl, setExistingUrl] = useState("");
  const [providerHistory, setProviderHistory] = useState({});
  const [loading, setLoading] = useState({ 
    fetch: false, 
    save: false, 
    check: false, 
    disconnect: false 
  });

  // Reset state when domainId changes
  useEffect(() => {
    if (!domainId) {
      setSelectedProvider(DEFAULT_PROVIDER);
      setCalendarUrl("");
      setHasExistingConnection(false);
      setExistingProvider("");
      setExistingUrl("");
      setProviderHistory({});
      setLoading({ fetch: false, save: false, check: false, disconnect: false });
    }
  }, [domainId]);

  const fetchActiveProvider = useCallback(async () => {
    if (!domainId) return;

    try {
      setLoading(prev => ({ ...prev, check: true }));
      const res = await axios.get(
        `${FAST_API_BASE_URL}/api/calendar-providers`,
        { headers: { "DOMAIN-TOKEN": domainId } }
      );

      if (res?.data?.resultCode === "0" && res.data.result?.provider) {
        const { provider, calendar_url } = res.data.result;
        setHasExistingConnection(true);
        setExistingProvider(provider);
        setExistingUrl(calendar_url);
        setSelectedProvider(provider);
        setCalendarUrl(calendar_url);
        setProviderHistory(prev => ({ ...prev, [provider]: calendar_url }));
      } else {
        setSelectedProvider(DEFAULT_PROVIDER);
        setHasExistingConnection(false);
        setExistingProvider("");
        setExistingUrl("");
      }
    } catch (err) {
      console.error("Failed to fetch active provider:", err);
      setSelectedProvider(DEFAULT_PROVIDER);
      setHasExistingConnection(false);
      setExistingProvider("");
      setExistingUrl("");
    } finally {
      setLoading(prev => ({ ...prev, check: false }));
    }
  }, [domainId]);

  const fetchProviderDetails = useCallback(async (providerId) => {
    if (!domainId || !providerId || providerId === existingProvider) return;

    try {
      setLoading(prev => ({ ...prev, fetch: true }));
      const res = await axios.get(
        `${FAST_API_BASE_URL}/api/calendar-providers/${providerId}`,
        { headers: { "DOMAIN-TOKEN": domainId } }
      );

      if (res?.data?.resultCode === "0" && res.data.result?.calendar_url) {
        setCalendarUrl(res.data.result.calendar_url);
        setProviderHistory(prev => ({ ...prev, [providerId]: res.data.result.calendar_url }));
      } else {
        setCalendarUrl("");
        setProviderHistory(prev => ({ ...prev, [providerId]: "" }));
      }
    } catch (err) {
      console.error(`Failed to fetch provider details for ${providerId}:`, err);
      setCalendarUrl("");
      setProviderHistory(prev => ({ ...prev, [providerId]: "" }));
    } finally {
      setLoading(prev => ({ ...prev, fetch: false }));
    }
  }, [domainId, existingProvider]);

  const saveProvider = useCallback(async (provider, url) => {
    if (provider === TEAM_SCHEDULING_PROVIDER) {
      url = "";
    } else if (!provider || !url.trim()) {
      return { success: false, message: "Please select a provider and enter a valid URL" };
    }

    try {
      setLoading(prev => ({ ...prev, save: true }));
      const res = await axios.post(
        `${FAST_API_BASE_URL}/api/calendar-providers`,
        { provider, calendar_url: url.trim()|| "N/A" },
        { headers: { "DOMAIN-TOKEN": domainId } }
      );
      
      if (res?.data?.resultCode === "0") {
        setHasExistingConnection(true);
        setExistingProvider(provider);
        setExistingUrl(url.trim());
        setProviderHistory(prev => ({ ...prev, [provider]: url.trim() }));
        return { success: true, message: "Provider connected successfully!" };
      } else {
        throw new Error(res?.data?.messages || "Failed to save provider");
      }
    } catch (err) {
      console.error("Failed to save provider:", err);
      return { 
        success: false, 
        message: err.response?.data?.messages || "Failed to connect. Please try again." 
      };
    } finally {
      setLoading(prev => ({ ...prev, save: false }));
    }
  }, [domainId]);

  const disconnectProvider = useCallback(async () => {
    try {
      setLoading(prev => ({ ...prev, disconnect: true }));
      const res = await axios.delete(
        `${FAST_API_BASE_URL}/api/calendar-providers`,
        {
          headers: { "DOMAIN-TOKEN": domainId },
          data: { provider: existingProvider },
        }
      );

      if (res?.data?.resultCode === "0") {
        setHasExistingConnection(false);
        setExistingProvider("");
        setExistingUrl("");
        setProviderHistory(prev => ({ ...prev, [existingProvider]: "" }));
        setSelectedProvider(DEFAULT_PROVIDER);
        setCalendarUrl("");
        return { success: true, message: "Disconnected successfully!" };
      } else {
        throw new Error(res?.data?.messages || "Failed to disconnect");
      }
    } catch (err) {
      console.error("Failed to disconnect:", err);
      return { 
        success: false, 
        message: err.response?.data?.messages || "Failed to disconnect. Please try again." 
      };
    } finally {
      setLoading(prev => ({ ...prev, disconnect: false }));
    }
  }, [existingProvider, domainId]);

  return {
    selectedProvider,
    setSelectedProvider,
    calendarUrl,
    setCalendarUrl,
    hasExistingConnection,
    existingProvider,
    existingUrl,
    loading,
    fetchActiveProvider,
    fetchProviderDetails,
    saveProvider,
    disconnectProvider
  };
};

const useTeamScheduling = (domainId) => {
  const [teamSchedulingStatus, setTeamSchedulingStatus] = useState(null);
  const [teamMembers, setTeamMembers] = useState([]);
  const [isTeamSchedulingEnabled, setIsTeamSchedulingEnabled] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [schedulingSettings, setSchedulingSettings] = useState({
    slot_duration_minutes: 30,
    buffer_before_minutes: 5,
    buffer_after_minutes: 5,
    working_hours_start: '09:00',
    working_hours_end: '17:00',
    working_days: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'],
    min_lead_time_hours: 2,
    max_booking_days: 90,
    timezone: 'UTC',
  });

  // Reset state when domainId changes
  useEffect(() => {
    if (!domainId) {
      setTeamSchedulingStatus(null);
      setTeamMembers([]);
      setIsTeamSchedulingEnabled(false);
      setSchedulingSettings({
        slot_duration_minutes: 30,
        buffer_before_minutes: 5,
        buffer_after_minutes: 5,
        working_hours_start: '09:00',
        working_hours_end: '17:00',
        working_days: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'],
        min_lead_time_hours: 2,
        max_booking_days: 90,
        timezone: 'UTC',
      });
    }
  }, [domainId]);

  const fetchTeamSchedulingStatus = useCallback(async () => {
    if (!domainId) return;
    
    try {
      setIsLoading(true);
      const res = await axios.get(`${FAST_API_BASE_URL}/api/team-scheduling/status`, {
        headers: { "DOMAIN-TOKEN": domainId },
      });
      
      if (res.data.success) {
        setTeamSchedulingStatus(res.data.data);
        setIsTeamSchedulingEnabled(res.data.data.can_accept_bookings);
        setTeamMembers(res.data.data.team_members || []);
        
        if (res.data.data.config) {
          setSchedulingSettings(res.data.data.config);
        }
      } else {
        setTeamSchedulingStatus(null);
        setIsTeamSchedulingEnabled(false);
      }
    } catch (err) {
      console.error("Failed to fetch team scheduling status:", err);
      setTeamSchedulingStatus(null);
      setIsTeamSchedulingEnabled(false);
    } finally {
      setIsLoading(false);
    }
  }, [domainId]);

  return {
    teamSchedulingStatus,
    teamMembers,
    isTeamSchedulingEnabled,
    setIsTeamSchedulingEnabled,
    schedulingSettings,
    setSchedulingSettings,
    fetchTeamSchedulingStatus,
    isLoading
  };
};
const useGoogleOAuth = (domainId, onSuccess) => {
  const [oauthLoading, setOauthLoading] = useState(false);
  const popupCheckIntervalRef = useRef(null);
  const messageListenerRef = useRef(null);

  const initiateOAuth = useCallback(async () => {
    if (!domainId) return;
    
    setOauthLoading(true);
    
    try {
      const res = await axios.get(`${FAST_API_BASE_URL}/api/google-calendar/auth`, {
        headers: { "DOMAIN-TOKEN": domainId },
      });
      
      if (res?.data?.result?.auth_url) {
        const width = 600;
        const height = 700;
        const left = (window.screen.width - width) / 2;
        const top = (window.screen.height - height) / 2;
        
        const popup = window.open(
          res.data.result.auth_url,
          'google_calendar_auth',
          `width=${width},height=${height},left=${left},top=${top},toolbar=no,location=no,menubar=no,scrollbars=yes,resizable=yes`
        );
        
        if (!popup || popup.closed || typeof popup.closed === 'undefined') {
          setOauthLoading(false);
          return { success: false, needsPopupHelp: true };
        }

        const handleMessage = (event) => {
          const validOrigins = [
            'http://localhost:8000',
            'http://localhost:3000',
            'https://api.ansera.ai',
          ];
          
          if (!validOrigins.includes(event.origin)) return;
          
          if (event.data.type === 'GOOGLE_CALENDAR_AUTH_SUCCESS') {
            if (messageListenerRef.current) {
              window.removeEventListener('message', messageListenerRef.current);
              messageListenerRef.current = null;
            }
            
            if (popupCheckIntervalRef.current) {
              clearInterval(popupCheckIntervalRef.current);
              popupCheckIntervalRef.current = null;
            }
            
            if (popup && !popup.closed) {
              popup.close();
            }
            
            setOauthLoading(false);
            onSuccess?.();
            return { success: true };
          } else if (event.data.type === 'GOOGLE_CALENDAR_AUTH_ERROR') {
            setOauthLoading(false);
            return { success: false, message: event.data.message };
          }
        };
        
        messageListenerRef.current = handleMessage;
        window.addEventListener('message', messageListenerRef.current);
        
        popupCheckIntervalRef.current = setInterval(() => {
          try {
            if (popup.closed) {
              if (popupCheckIntervalRef.current) {
                clearInterval(popupCheckIntervalRef.current);
                popupCheckIntervalRef.current = null;
              }
              
              if (messageListenerRef.current) {
                window.removeEventListener('message', messageListenerRef.current);
                messageListenerRef.current = null;
              }
              
              if (oauthLoading) {
                setOauthLoading(false);
              }
            }
          } catch (e) {
            if (popupCheckIntervalRef.current) {
              clearInterval(popupCheckIntervalRef.current);
              popupCheckIntervalRef.current = null;
            }
          }
        }, 1000);
        
        return { success: true };
      } else {
        throw new Error("No auth URL received");
      }
    } catch (err) {
      console.error("Failed to initiate Google auth:", err);
      setOauthLoading(false);
      return { success: false, message: "Failed to connect to Google Calendar." };
    }
  }, [domainId, onSuccess]);

  useEffect(() => {
    return () => {
      if (messageListenerRef.current) {
        window.removeEventListener('message', messageListenerRef.current);
        messageListenerRef.current = null;
      }
      
      if (popupCheckIntervalRef.current) {
        clearInterval(popupCheckIntervalRef.current);
        popupCheckIntervalRef.current = null;
      }
    };
  }, []);

  return {
    oauthLoading,
    initiateOAuth
  };
};

const Integrations = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const { currentDomain } = useAuth();
  const domainId = currentDomain?.domain_token;
  
  const [activeTab, setActiveTab] = useState(0);
  const [showVideo, setShowVideo] = useState(false);
  const [currentVideoUrl, setCurrentVideoUrl] = useState("");
  const [urlError, setUrlError] = useState("");
  const [snackbar, setSnackbar] = useState({
    open: false,
    severity: "success",
    message: "",
  });
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    action: "",
    data: null,
  });
  const [teamManagementModalOpen, setTeamManagementModalOpen] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);
  const [showPopupHelp, setShowPopupHelp] = useState(false);
  const [isSavingTeamProvider, setIsSavingTeamProvider] = useState(false);
  
  const googleStatus = useGoogleCalendarStatus(domainId);
  const singleProvider = useSingleProvider(domainId);
  const teamScheduling = useTeamScheduling(domainId);
  
  const googleOAuth = useGoogleOAuth(domainId, () => {
    googleStatus.checkGoogleCredentials();
    
    if (activeTab === 1 || singleProvider.existingProvider === TEAM_SCHEDULING_PROVIDER) {
      teamScheduling.fetchTeamSchedulingStatus();
    }
    
    setSnackbar({
      open: true,
      severity: "success",
      message: "Google Calendar connected successfully!",
    });
  });

  const getEmbedUrlForProvider = useCallback((providerId) => {
    const shareUrl = LOOM_SHARE_URLS[providerId];
    if (!shareUrl) return null;
    return getLoomEmbedUrl(shareUrl);
  }, []);

  const providerHasVideo = useCallback((providerId) => {
    return calendarProviders.find(p => p.id === providerId)?.hasVideo && LOOM_SHARE_URLS[providerId];
  }, []);

  const validateUrl = useCallback((provider, url) => {
    const trimmedUrl = url.trim().toLowerCase();
    
    if (provider === TEAM_SCHEDULING_PROVIDER) {
      return "";
    }
    
    if (!trimmedUrl) {
      return "URL is required";
    }

    try {
      new URL(trimmedUrl);
    } catch (err) {
      return "Please enter a valid URL starting with http:// or https://";
    }

    const providerPatterns = {
      "google-calendar": [/calendar\.google\.com/, /calendar\.app\.google/],
      "calendly": [/calendly\.com/],
      "zoho-calendar": [/calendar\.zoho\.com/, /zoho\.com\/calendar/],
      "contact-form": [],
      "team-scheduling": []
    };

    const patterns = providerPatterns[provider];
    
    if (patterns && patterns.length > 0) {
      const isValid = patterns.some(pattern => pattern.test(trimmedUrl));
      if (!isValid) {
        const providerName = calendarProviders.find(p => p.id === provider)?.name || provider;
        return `Please enter a valid ${providerName} URL`;
      }
    }

    return "";
  }, []);
 useEffect(() => {
    if (domainId) {
      // Refresh all data when domainId changes
      singleProvider.fetchActiveProvider();
      googleStatus.checkGoogleCredentials();
      
      // Only fetch team scheduling if on that tab or if team scheduling is active
      if (activeTab === 1 || singleProvider.existingProvider === TEAM_SCHEDULING_PROVIDER) {
        teamScheduling.fetchTeamSchedulingStatus();
      }
    } else {
      // Reset states if no domainId
      singleProvider.setSelectedProvider(DEFAULT_PROVIDER);
      singleProvider.setCalendarUrl("");
      singleProvider.setHasExistingConnection(false);
      singleProvider.setExistingProvider("");
      singleProvider.setExistingUrl("");
      singleProvider.setProviderHistory({});
      
      setActiveTab(0);
      setShowVideo(false);
      setCurrentVideoUrl("");
      setUrlError("");
      setTeamManagementModalOpen(false);
      setShowSettings(false);
      setShowPopupHelp(false);
    }
  }, [domainId]); 
  useEffect(() => {
    if (singleProvider.existingProvider === TEAM_SCHEDULING_PROVIDER) {
      setActiveTab(1);
    }
  }, [singleProvider.existingProvider]);

  useEffect(() => {
    if (domainId) {
      singleProvider.fetchActiveProvider();
    }
  }, [domainId]);

  useEffect(() => {
    if (domainId && (activeTab === 1 || singleProvider.existingProvider === TEAM_SCHEDULING_PROVIDER) && !teamScheduling.teamSchedulingStatus) {
      teamScheduling.fetchTeamSchedulingStatus();
      googleStatus.checkGoogleCredentials();
    }
  }, [domainId, activeTab, singleProvider.existingProvider]);

  useEffect(() => {
    if (singleProvider.selectedProvider && singleProvider.selectedProvider !== singleProvider.existingProvider) {
      const timer = setTimeout(() => {
        singleProvider.fetchProviderDetails(singleProvider.selectedProvider);
      }, 500);
      
      return () => clearTimeout(timer);
    }
  }, [singleProvider.selectedProvider]);

  const handleTabChange = useCallback((event, newValue) => {
    setActiveTab(newValue);
    setShowVideo(false);
    setCurrentVideoUrl("");
  }, []);

  const handleProviderChange = useCallback((event) => {
    const providerId = event.target.value;
    singleProvider.setSelectedProvider(providerId);
    setUrlError("");
    setShowVideo(false);
    setCurrentVideoUrl("");
  }, [singleProvider]);

  const handleUrlChange = useCallback((e) => {
    const value = e.target.value;
    singleProvider.setCalendarUrl(value);
    if (singleProvider.selectedProvider) {
      const error = validateUrl(singleProvider.selectedProvider, value);
      setUrlError(error);
    }
  }, [singleProvider.selectedProvider, validateUrl, singleProvider]);

  const openProviderWebsite = useCallback(() => {
    const urls = {
      "google-calendar": "https://calendar.google.com/calendar/u/0/r",
      "calendly": "https://calendly.com",
      "zoho-calendar": "https://calendar.zoho.com",
    };
    if (singleProvider.selectedProvider && urls[singleProvider.selectedProvider]) {
      window.open(urls[singleProvider.selectedProvider], "_blank");
    }
  }, [singleProvider.selectedProvider]);

  const handleWatchVideo = useCallback(() => {
    const embedUrl = getEmbedUrlForProvider(singleProvider.selectedProvider);
    if (embedUrl) {
      setCurrentVideoUrl(embedUrl);
      setShowVideo(true);
      
      if (isMobile) {
        setTimeout(() => {
          document.querySelector('.video-section')?.scrollIntoView({ 
            behavior: 'smooth',
            block: 'center'
          });
        }, 100);
      }
    }
  }, [singleProvider.selectedProvider, getEmbedUrlForProvider, isMobile]);

  const handleConnectGoogle = useCallback(async () => {
    const result = await googleOAuth.initiateOAuth();
    
    if (result.needsPopupHelp) {
      setShowPopupHelp(true);
      setSnackbar({
        open: true,
        severity: "warning",
        message: "Popup was blocked. Please enable popups for this site.",
      });
    } else if (!result.success) {
      setSnackbar({
        open: true,
        severity: "error",
        message: result.message || "Failed to connect Google Calendar.",
      });
    }
  }, [googleOAuth]);

  const handleSaveTeamSchedulingProvider = useCallback(async () => {
    setIsSavingTeamProvider(true);
    const result = await singleProvider.saveProvider(TEAM_SCHEDULING_PROVIDER, "");
    
    setSnackbar({
      open: true,
      severity: result.success ? "success" : "error",
      message: result.success ? "Team scheduling enabled as active provider!" : result.message,
    });
    
    if (result.success) {
      setActiveTab(1);
      await teamScheduling.fetchTeamSchedulingStatus();
    }
    
    setIsSavingTeamProvider(false);
  }, [singleProvider, teamScheduling]);

  const handleSaveSettings = useCallback(async () => {
    setSavingSettings(true);
    try {
      const res = await axios.post(
        `${FAST_API_BASE_URL}/api/team-scheduling/config`,
        teamScheduling.schedulingSettings,
        { headers: { "DOMAIN-TOKEN": domainId } }
      );
      
      if (res.data.success) {
        setSnackbar({
          open: true,
          severity: "success",
          message: "Settings saved successfully!",
        });
        setShowSettings(false);
        teamScheduling.fetchTeamSchedulingStatus();
      } else {
        throw new Error("Failed to save settings");
      }
    } catch (err) {
      console.error("Failed to save settings:", err);
      setSnackbar({
        open: true,
        severity: "error",
        message: "Failed to save settings. Please try again.",
      });
    } finally {
      setSavingSettings(false);
    }
  }, [teamScheduling, domainId]);

  const handleEnableTeamScheduling = useCallback(async () => {
    if (!googleStatus.hasGoogleCredentials) {
      setSnackbar({
        open: true,
        severity: "error",
        message: "Please connect Google Calendar first to use team scheduling.",
      });
      return;
    }

    if (!teamScheduling.teamSchedulingStatus?.is_configured) {
      setTeamManagementModalOpen(true);
    } else {
      await handleSaveTeamSchedulingProvider();
    }
  }, [googleStatus.hasGoogleCredentials, teamScheduling.teamSchedulingStatus, handleSaveTeamSchedulingProvider]);

  const handleSaveProvider = useCallback(async () => {
    if (singleProvider.selectedProvider === TEAM_SCHEDULING_PROVIDER) {
      await handleSaveTeamSchedulingProvider();
      return;
    }

    if (!singleProvider.selectedProvider || !singleProvider.calendarUrl.trim()) {
      setSnackbar({
        open: true,
        severity: "error",
        message: "Please select a provider and enter a valid URL",
      });
      return;
    }

    const error = validateUrl(singleProvider.selectedProvider, singleProvider.calendarUrl);
    if (error) {
      setUrlError(error);
      return;
    }

    if (singleProvider.hasExistingConnection && singleProvider.existingProvider !== singleProvider.selectedProvider) {
      setConfirmDialog({
        open: true,
        action: "update",
        data: { newProvider: singleProvider.selectedProvider },
      });
      return;
    }

    const result = await singleProvider.saveProvider(singleProvider.selectedProvider, singleProvider.calendarUrl);
    setSnackbar({
      open: true,
      severity: result.success ? "success" : "error",
      message: result.message,
    });
  }, [singleProvider, validateUrl, handleSaveTeamSchedulingProvider]);

  const handleDisconnect = useCallback(async () => {
    const result = await singleProvider.disconnectProvider();
    setSnackbar({
      open: true,
      severity: result.success ? "success" : "error",
      message: result.message,
    });
    setConfirmDialog({ open: false, action: "" });
  }, [singleProvider]);

  const isContactFormSelected = singleProvider.selectedProvider === "contact-form";
  const hasVideo = providerHasVideo(singleProvider.selectedProvider);
  const isTeamSchedulingTab = activeTab === 1;
  const isTeamSchedulingActive = singleProvider.existingProvider === TEAM_SCHEDULING_PROVIDER;

  if (singleProvider.loading.check && activeTab === 0) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <SoftBox
          sx={{
            backgroundColor: "#fff",
            p: 3,
            borderRadius: '16px',
            boxShadow: "0px 2px 12px rgba(0,0,0,0.04)",
            minHeight: "500px",
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Box sx={{ textAlign: 'center' }}>
            <CircularProgress sx={{ color: "#219297", mb: 2 }} />
            <Typography sx={{ color: "#666" }}>Loading integration settings...</Typography>
          </Box>
        </SoftBox>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox
        sx={{
          backgroundColor: "#fff",
          p: { xs: 2, md: 3 },
          borderRadius: '16px',
          boxShadow: "0px 2px 12px rgba(0,0,0,0.04)",
          minHeight: "500px",
          width: '100%',
        }}
      >
        <Dialog
          open={confirmDialog.open}
          onClose={() => setConfirmDialog({ open: false, action: "" })}
          PaperProps={{
            sx: {
              borderRadius: '12px',
              padding: 1,
              maxWidth: '90vw',
              width: 'min(500px, 90vw)',
              m: 2,
            },
          }}
        >
          <DialogTitle
            sx={{
              fontWeight: "600",
              color: "#219297",
              padding: 2,
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              fontSize: '1.1rem',
            }}
          >
            <Info sx={{ color: "#ff9800", fontSize: 22 }} />
            {confirmDialog.action === "disconnect" ? "Disconnect?" : 
             confirmDialog.action === "update" ? "Update Connection?" :
             "Confirm Action"}
          </DialogTitle>
          <DialogContent sx={{ padding: '8px 16px' }}>
            <DialogContentText
              sx={{ 
                color: "#666", 
                fontSize: '0.9rem', 
                lineHeight: 1.6 
              }}
            >
              {confirmDialog.action === "disconnect" 
                ? "Are you sure you want to disconnect? This will remove all integration settings."
                : "You already have a provider connected. Connecting a new one will replace the existing connection."}
            </DialogContentText>
          </DialogContent>
          <DialogActions sx={{ 
            padding: '12px 16px', 
            gap: 1,
            flexDirection: { xs: 'column', sm: 'row' }
          }}>
            <Button
              onClick={() => setConfirmDialog({ open: false, action: "" })}
              fullWidth={isMobile}
              sx={{
                color: "#666",
                fontWeight: 600,
                textTransform: "none",
                borderRadius: "8px",
                padding: '8px 16px',
                fontSize: '0.9rem',
                "&:hover": {
                  backgroundColor: "#f5f5f5",
                },
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={() => {
                if (confirmDialog.action === "disconnect") {
                  handleDisconnect();
                } else if (confirmDialog.action === "update") {
                  handleSaveProvider();
                  setConfirmDialog({ open: false, action: "" });
                }
              }}
              variant="contained"
              fullWidth={isMobile}
              sx={{
                backgroundColor: "#219297",
                fontWeight: 600,
                textTransform: "none",
                color: "#fff",
                borderRadius: "8px",
                padding: '8px 16px',
                fontSize: '0.9rem',
                "&:hover": {
                  backgroundColor: "#1a767a",
                },
              }}
            >
              {confirmDialog.action === "disconnect" 
                ? "Disconnect"
                : "Confirm"}
            </Button>
          </DialogActions>
        </Dialog>

        <Collapse in={showPopupHelp}>
          <Alert 
            severity="info" 
            sx={{ 
              mb: 3, 
              borderRadius: '16px',
              border: '2px solid #219297',
              backgroundColor: '#e0f5f5',
              boxShadow: '0px 4px 12px rgba(33, 146, 151, 0.15)',
            }}
            icon={<InfoOutlined sx={{ fontSize: 28, color: '#219297' }} />}
            onClose={() => setShowPopupHelp(false)}
          >
            <Typography variant="body1" fontWeight={700} gutterBottom sx={{ color: '#145d61', mb: 2 }}>
              🚫 Popup Blocked - Enable Popups to Continue
            </Typography>
            
            <Typography variant="body2" sx={{ color: '#2c5f5f', mb: 2, lineHeight: 1.6 }}>
              Your browser blocked the authorization window. Please allow popups for this site to connect your Google Calendar.
            </Typography>

            <Box sx={{ 
              backgroundColor: '#fff', 
              borderRadius: '12px', 
              p: 2.5, 
              border: '1px solid #a8d5d8' 
            }}>
              <Typography variant="body2" fontWeight={600} sx={{ color: '#219297', mb: 2 }}>
                How to Enable Popups:
              </Typography>
              
              <Box component="ul" sx={{ m: 0, pl: 2.5, '& li': { mb: 1.5 } }}>
                <li>
                  <Typography variant="body2" sx={{ color: '#424242' }}>
                    <strong style={{ color: '#219297' }}>Chrome / Edge:</strong> Look for the popup icon 
                    <Box component="span" sx={{ 
                      display: 'inline-block', 
                      mx: 0.5, 
                      px: 1, 
                      py: 0.3, 
                      borderRadius: '4px',
                      fontFamily: 'monospace',
                    }}>
                      <CreditCardOff/>
                    </Box> 
                    in your address bar (top right), click it, and select <strong>"Always allow popups from this site"</strong>
                  </Typography>
                </li>
                
                <li>
                  <Typography variant="body2" sx={{ color: '#424242' }}>
                    <strong style={{ color: '#1a767a' }}>Firefox:</strong> Click the <strong>"Preferences"</strong> or <strong>"Options"</strong> button in the address bar, then select <strong>"Allow popups for this site"</strong>
                  </Typography>
                </li>
                
                <li>
                  <Typography variant="body2" sx={{ color: '#424242' }}>
                    <strong style={{ color: '#0d5457' }}>Safari:</strong> Go to <strong>Safari menu</strong> → <strong>Settings</strong> → <strong>Websites</strong> → <strong>Pop-up Windows</strong>, then set this site to <strong>"Allow"</strong>
                  </Typography>
                </li>
              </Box>
            </Box>
          </Alert>
        </Collapse>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
          TransitionComponent={(props) => <Slide {...props} direction="down" />}
        >
          <Alert
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            severity={snackbar.severity}
            variant="filled"
            sx={{
              backgroundColor:
                snackbar.severity === "success" ? "#219297" : 
                snackbar.severity === "error" ? "#d32f2f" :
                snackbar.severity === "info" ? "#1976d2" : "#219297",
              color: "#fff",
              borderRadius: '10px',
              fontSize: '0.9rem',
              maxWidth: '90vw',
            }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>

        <SoftBox mb={3}>
          <SoftBox display="flex" alignItems="center" gap={2} mb={1}>
            <Box
              sx={{
                borderRadius: '10px',
                p: 0.5,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <img 
                src={AnseraLogo} 
                alt="Ansera Logo" 
                style={{ 
                  width: '48px',
                  height: 'auto'
                }} 
              />
            </Box>
            <SoftTypography 
              variant="h5"
              fontWeight="bold" 
              color="dark"
              sx={{ fontSize: { xs: '1.25rem', md: '1.5rem' } }}
            >
              Scheduling Integration
            </SoftTypography>
          </SoftBox>
          <SoftTypography
            variant="body2"
            color="text"
            sx={{ 
              opacity: 0.7, 
              fontSize: '0.9rem'
            }}
          >
            Connect your calendar or setup team scheduling
          </SoftTypography>
        </SoftBox>

        <Box sx={{ 
          borderBottom: 1, 
          borderColor: '#e0e0e0', 
          mb: 3,
          '& .MuiTabs-indicator': {
            backgroundColor: '#219297',
            height: '3px',
          }
        }}>
          <Tabs 
            value={activeTab} 
            onChange={handleTabChange}
            sx={{
              '& .MuiTab-root': {
                textTransform: 'none',
                fontWeight: 600,
                fontSize: { xs: '0.9rem', md: '1rem' },
                minWidth: { xs: 'auto', md: 160 },
                padding: { xs: '12px 8px', md: '12px 16px' },
                color: '#666',
                '&.Mui-selected': {
                  color: '#219297',
                },
              }
            }}
          >
            <Tab label="Single Calendar" />
            <Tab label="Team Scheduling" />
          </Tabs>
        </Box>

        {activeTab === 0 && (
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>

              <Box sx={{ mb: 3 }}>
                <Typography 
                  variant="subtitle1" 
                  sx={{ 
                    mb: 1.5, 
                    color: '#219297',
                    fontWeight: 600,
                    fontSize: { xs: '0.9rem', md: '0.95rem' }
                  }}
                >
                  Select Calendar Provider
                </Typography>
                
                <FormControl fullWidth>
                  <Select
                    value={singleProvider.selectedProvider}
                    onChange={handleProviderChange}
                    disabled={singleProvider.loading.fetch || singleProvider.loading.check}
                    sx={{
                      borderRadius: '8px',
                      backgroundColor: '#f8fafa',
                      border: '1px solid #e0e0e0',
                      '&:hover': {
                        borderColor: '#219297',
                      },
                      '&.Mui-focused': {
                        borderColor: '#219297',
                        boxShadow: '0 0 0 3px rgba(33, 146, 151, 0.1)',
                      },
                      '& .MuiOutlinedInput-notchedOutline': {
                        border: 'none',
                      },
                    }}
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          borderRadius: '8px',
                          marginTop: '8px',
                          boxShadow: '0px 4px 16px rgba(0,0,0,0.08)',
                          maxHeight: 400,
                        },
                      },
                    }}
                    renderValue={(selected) => {
                      const provider = calendarProviders.find(p => p.id === selected);
                      return (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Avatar
                            src={provider?.logo}
                            sx={{
                              width: 32,
                              height: 32,
                              backgroundColor: `${provider?.color || '#219297'}15`,
                              color: provider?.color || '#219297',
                            }}
                          >
                            {provider?.icon}
                          </Avatar>
                          <Typography variant="body1" fontWeight="medium" color="text.primary">
                            {provider?.name || selected}
                          </Typography>
                        </Box>
                      );
                    }}
                  >
                    {calendarProviders
                      .filter(p => p.type === "single-user")
                      .map((provider) => (
                      <MenuItem key={provider.id} value={provider.id}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                          <Avatar
                            src={provider.logo}
                            sx={{
                              width: 32,
                              height: 32,
                              backgroundColor: `${provider.color}15`,
                              color: provider.color,
                            }}
                          >
                            {provider.icon}
                          </Avatar>
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="body1" fontWeight="medium" color="text.primary">
                              {provider.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" noWrap>
                              {provider.description}
                            </Typography>
                          </Box>
                          {singleProvider.hasExistingConnection && singleProvider.existingProvider === provider.id && (
                            <CheckCircle sx={{ color: '#2e7d32', fontSize: 20 }} />
                          )}
                          {singleProvider.loading.fetch && singleProvider.selectedProvider === provider.id && (
                            <CircularProgress size={16} sx={{ color: '#219297' }} />
                          )}
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>

              {singleProvider.selectedProvider && singleProvider.selectedProvider !== TEAM_SCHEDULING_PROVIDER && (
                <Box sx={{ mb: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5, flexWrap: 'wrap', gap: 1 }}>
                    <Typography 
                      variant="subtitle1" 
                      sx={{ 
                        color: '#219297',
                        fontWeight: 600,
                        fontSize: { xs: '0.9rem', md: '0.95rem' }
                      }}
                    >
                      {providerLabels[singleProvider.selectedProvider] || "Calendar URL"}
                    </Typography>
                    
                    {!isContactFormSelected && (
                      <Button
                        startIcon={<CalendarMonth />}
                        onClick={openProviderWebsite}
                        size="small"
                        sx={{
                          color: '#219297',
                          textTransform: 'none',
                          fontSize: '0.8rem',
                          fontWeight: 600,
                          '&:hover': {
                            backgroundColor: '#e0faf3ff',
                            color: '#1a767a',
                          },
                        }}
                      >
                        Open {calendarProviders.find(p => p.id === singleProvider.selectedProvider)?.name || 'Provider'}
                      </Button>
                    )}
                  </Box>

                  <CustomInput
                    value={singleProvider.calendarUrl}
                    onChange={handleUrlChange}
                    placeholder={providerPlaceholders[singleProvider.selectedProvider] || "Enter your calendar URL"}
                    error={!!urlError}
                    helperText={urlError || (isContactFormSelected 
                      ? "Enter the URL of your contact page or form" 
                      : "Make sure the URL is publicly accessible")}
                    disabled={singleProvider.loading.save || singleProvider.loading.fetch}
                    startIcon={<Link sx={{ color: '#219297' }} />}
                    endAdornment={
                      singleProvider.calendarUrl && (
                        <>
                          <IconButton
                            size="small"
                            onClick={() => singleProvider.setCalendarUrl("")}
                            sx={{ p: 0.5 }}
                          >
                            <Close fontSize="small" />
                          </IconButton>
                          <Tooltip title="Copy URL">
                            <IconButton
                              size="small"
                              onClick={() => {
                                navigator.clipboard.writeText(singleProvider.calendarUrl);
                                setSnackbar({
                                  open: true,
                                  severity: "success",
                                  message: "URL copied to clipboard!",
                                });
                              }}
                              sx={{ p: 0.5 }}
                            >
                              <ContentCopy fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </>
                      )
                    }
                  />

                  {/* {providerExamples[singleProvider.selectedProvider] && (
                    <Box sx={{ 
                      mt: 1, 
                      p: 1.5, 
                      backgroundColor: '#e3f2fd', 
                      borderRadius: '8px',
                      border: '1px solid #bbdefb'
                    }}>
                      <Typography variant="caption" sx={{ color: '#1565c0', fontWeight: 600, display: 'block', mb: 0.5 }}>
                        Example URL:
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#0d47a1', fontFamily: 'monospace', fontSize: '0.75rem' }}>
                        {providerExamples[singleProvider.selectedProvider]}
                      </Typography>
                    </Box>
                  )} */}

                  {providerPricing[singleProvider.selectedProvider] && (
                    <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                      <InfoOutlined sx={{ color: '#666', fontSize: 16 }} />
                      <Typography variant="caption" sx={{ color: '#666' }}>
                        Pricing: {providerPricing[singleProvider.selectedProvider]}
                      </Typography>
                    </Box>
                  )}
                </Box>
              )}

              <Box sx={{ 
                display: 'flex', 
                gap: 2,
                flexDirection: { xs: 'column', sm: 'row' }
              }}>
                {singleProvider.hasExistingConnection && singleProvider.existingProvider !== TEAM_SCHEDULING_PROVIDER ? (
                  <>
                    <Button
                      variant="contained"
                      fullWidth
                      onClick={handleSaveProvider}
                      disabled={singleProvider.loading.save || !singleProvider.calendarUrl.trim() || !!urlError}
                      sx={{
                        backgroundColor: "#219297",
                        fontWeight: 600,
                        textTransform: "none",
                        color: "#fff",
                        borderRadius: "8px",
                        padding: { xs: "10px 16px", md: "12px 24px" },
                        fontSize: { xs: '0.9rem', md: '0.95rem' },
                        "&:hover": {
                          backgroundColor: "#1a767a",
                        },
                        "&.Mui-disabled": {
                          backgroundColor: "#e0e0e0",
                          color: "#999",
                        },
                      }}
                    >
                      {singleProvider.loading.save ? (
                        <CircularProgress size={20} sx={{ color: "#fff" }} />
                      ) : singleProvider.existingProvider !== singleProvider.selectedProvider ? (
                        "Update Connection"
                      ) : (
                        "Update URL"
                      )}
                    </Button>
                    
                    <Button
                      variant="outlined"
                      fullWidth
                      onClick={() => setConfirmDialog({ open: true, action: "disconnect" })}
                      disabled={singleProvider.loading.disconnect}
                      sx={{
                        borderColor: "#d32f2f",
                        color: "#d32f2f",
                        fontWeight: 600,
                        textTransform: "none",
                        borderRadius: "8px",
                        padding: { xs: "10px 16px", md: "12px 24px" },
                        fontSize: { xs: '0.9rem', md: '0.95rem' },
                        "&:hover": {
                          backgroundColor: "#b84758ff",
                          borderColor: "#b71c1c",
                          color: "#ffffff",
                        },
                      }}
                    >
                      {singleProvider.loading.disconnect ? "Disconnecting..." : "Disconnect"}
                    </Button>
                  </>
                ) : singleProvider.selectedProvider === TEAM_SCHEDULING_PROVIDER ? (
                  <Button
                    variant="contained"
                    fullWidth
                    onClick={() => {
                      setActiveTab(1);
                    }}
                    sx={{
                      backgroundColor: "#219297",
                      fontWeight: 600,
                      textTransform: "none",
                      color: "#fff",
                      borderRadius: "8px",
                      padding: { xs: "10px 16px", md: "12px 24px" },
                      fontSize: { xs: '0.9rem', md: '0.95rem' },
                      "&:hover": {
                        backgroundColor: "#1a767a",
                      },
                    }}
                  >
                    Go to Team Scheduling
                  </Button>
                ) : (
                  <Button
                    variant="contained"
                    fullWidth
                    onClick={handleSaveProvider}
                    disabled={singleProvider.loading.save || !singleProvider.calendarUrl.trim() || !!urlError}
                    sx={{
                      backgroundColor: "#219297",
                      fontWeight: 600,
                      textTransform: "none",
                      color: "#fff",
                      borderRadius: "8px",
                      padding: { xs: "10px 16px", md: "12px 24px" },
                      fontSize: { xs: '0.9rem', md: '0.95rem' },
                      "&:hover": {
                        backgroundColor: "#1a767a",
                      },
                      "&.Mui-disabled": {
                        backgroundColor: "#e0e0e0",
                        color: "#999",
                      },
                    }}
                  >
                    {singleProvider.loading.save ? (
                      <CircularProgress size={20} sx={{ color: "#fff" }} />
                    ) : (
                      `Connect ${calendarProviders.find(p => p.id === singleProvider.selectedProvider)?.name || 'Provider'}`
                    )}
                  </Button>
                )}
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              {singleProvider.selectedProvider && singleProvider.selectedProvider !== TEAM_SCHEDULING_PROVIDER && (
                <Paper
                  sx={{
                    p: { xs: 2, md: 2.5 },
                    borderRadius: '12px',
                    backgroundColor: '#f8fafa',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    border: '1px solid #e0f0f0'
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                    <Avatar
                      src={calendarProviders.find(p => p.id === singleProvider.selectedProvider)?.logo}
                      sx={{
                        width: 48,
                        height: 48,
                        backgroundColor: `${calendarProviders.find(p => p.id === singleProvider.selectedProvider)?.color || '#219297'}15`,
                        color: calendarProviders.find(p => p.id === singleProvider.selectedProvider)?.color || '#219297',
                      }}
                    >
                      {calendarProviders.find(p => p.id === singleProvider.selectedProvider)?.icon}
                    </Avatar>
                    <Box>
                      <Typography variant="h6" fontWeight="600" sx={{ color: '#219297', fontSize: { xs: '1rem', md: '1.1rem' } }}>
                        {calendarProviders.find(p => p.id === singleProvider.selectedProvider)?.name}
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#666', fontSize: { xs: '0.75rem', md: '0.85rem' } }}>
                        {calendarProviders.find(p => p.id === singleProvider.selectedProvider)?.description}
                      </Typography>
                    </Box>
                  </Box>
                  {hasVideo && (
                    <Box className="video-section" sx={{ mb: 3 }}>
                      <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'space-between',
                        mb: 1.5 
                      }}>
                        <Typography 
                          variant="subtitle1" 
                          sx={{ 
                            color: '#219297',
                            fontWeight: 600,
                            fontSize: { xs: '0.9rem', md: '0.95rem' }
                          }}
                        >
                          Setup Guide Video
                        </Typography>
                        <Button
                          startIcon={<PlayCircleOutline />}
                          onClick={handleWatchVideo}
                          size="small"
                          sx={{
                            color: '#219297',
                            textTransform: 'none',
                            fontSize: '0.8rem',
                            fontWeight: 600,
                            '&:hover': {
                              backgroundColor: '#e0f7fa',
                            },
                          }}
                        >
                          Watch Guide
                        </Button>
                      </Box>
                      
                      <Collapse in={showVideo}>
                        <Box sx={{ 
                          position: 'relative', 
                          width: '100%', 
                          pt: '56.25%',
                          borderRadius: '8px',
                          overflow: 'hidden',
                          backgroundColor: '#000',
                          mb: 1.5
                        }}>
                          {currentVideoUrl && (
                            <iframe
                              src={currentVideoUrl}
                              style={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                width: '100%',
                                height: '100%',
                                border: 'none',
                              }}
                              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                              allowFullScreen
                              title="Setup Guide Video"
                            />
                          )}
                        </Box>
                      </Collapse>
                      
                      <Typography variant="caption" sx={{ color: '#666', display: 'block' }}>
                        Watch this short guide to learn how to set up your {calendarProviders.find(p => p.id === singleProvider.selectedProvider)?.name} integration.
                      </Typography>
                    </Box>
                  )}

                  <Box sx={{ flex: 1 }}>
                    <Typography 
                      variant="subtitle1" 
                      sx={{ 
                        mb: 2, 
                        color: '#219297',
                        fontWeight: 600,
                        fontSize: { xs: '0.9rem', md: '0.95rem' },
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1
                      }}
                    >
                      <InfoOutlined fontSize="small" />
                      Setup Instructions
                    </Typography>
                    
                    <Box sx={{ 
                      backgroundColor: '#fff', 
                      borderRadius: '8px', 
                      p: 2.5,
                      border: '1px solid #e0f0f0'
                    }}>
                      {providerInstructions[singleProvider.selectedProvider]?.map((step, index) => (
                        <InstructionStep
                          key={index}
                          number={index + 1}
                          title={step.title}
                        >
                          {step.content}
                        </InstructionStep>
                      ))}

                      {isContactFormSelected && (
                        <Box sx={{ 
                          mt: 2, 
                          p: 1.5, 
                          backgroundColor: '#fff3e0', 
                          borderRadius: '6px',
                          border: '1px solid #ffcc80'
                        }}>
                          <Typography variant="caption" sx={{ color: '#e65100', fontWeight: 600, display: 'block', mb: 0.5 }}>
                            Important:
                          </Typography>
                          <Typography variant="caption" sx={{ color: '#e65100' }}>
                            Your contact form should include fields for name, email, and message. 
                            Ansera will monitor this page for new inquiries and notify you.
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </Box>
                </Paper>
              )}
            </Grid>
          </Grid>
        )}

        {activeTab === 1 && (
          <Grid container spacing={3}>
            <Grid item xs={12} md={8}>
              <Paper sx={{ 
                p: { xs: 2, md: 3 }, 
                borderRadius: '12px', 
                backgroundColor: '#f8fafa',
                border: '1px solid #e0f0f0'
              }}>
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'space-between', 
                  mb: 3,
                  flexDirection: { xs: 'column', sm: 'row' },
                  gap: { xs: 2, sm: 0 }
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Avatar
                      sx={{
                        width: 48,
                        height: 48,
                        backgroundColor: '#219297',
                      }}
                    >
                      <Groups />
                    </Avatar>
                    <Box>
                      <Typography variant="h6" fontWeight="600" sx={{ color: '#219297', fontSize: { xs: '1rem', md: '1.1rem' } }}>
                        Team Scheduling
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#666', fontSize: { xs: '0.75rem', md: '0.85rem' } }}>
                        Round-robin scheduling with multiple team members
                      </Typography>
                    </Box>
                  </Box>
                  
                  {isTeamSchedulingActive && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Chip
                        label="Active"
                        size="small"
                        sx={{
                          backgroundColor: "#2e7d32",
                          color: "#fff",
                          fontSize: "0.7rem",
                          fontWeight: 600,
                        }}
                      />
                      <Button
                        variant="outlined"
                        color="error"
                        size="small"
                        onClick={() => setConfirmDialog({ open: true, action: "disconnect" })}
                        sx={{
                          textTransform: 'none',
                          fontSize: '0.75rem',
                          backgroundColor: '#b41d1dff',
                          color: '#ffffff',
                            "&:hover": {
                          backgroundColor: "#9c091fff",
                          borderColor: "#b71c1c",
                          color: "#ffffff",
                        },
                        }}
                      >
                        Disconnect
                      </Button>
                    </Box>
                  )}
                </Box>


                <Box sx={{ 
                  mb: 3, 
                  p: { xs: 2, md: 2.5 }, 
                  backgroundColor: '#f8fafa', 
                  borderRadius: '12px',
                  border: '1px solid #e0f0f0'
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Avatar
                        src="https://upload.wikimedia.org/wikipedia/commons/a/a5/Google_Calendar_icon_%282020%29.svg"
                        sx={{
                          width: 40,
                          height: 40,
                          backgroundColor: '#fff',
                          color: '#219297',
                          border: '1px solid #e0e0e0'
                        }}
                      >
                        <Google />
                      </Avatar>
                      <Box>
                        <Typography variant="h6" fontWeight="600" sx={{ color: '#219297', fontSize: { xs: '1rem', md: '1.1rem' } }}>
                          Google Calendar Connection
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#666', fontSize: { xs: '0.75rem', md: '0.85rem' } }}>
                          Required for team scheduling
                        </Typography>
                      </Box>
                    </Box>
                    {googleStatus.isLoading ? (
                      <CircularProgress size={24} sx={{ color: '#219297' }} />
                    ) : (
                      <Chip
                        label={googleStatus.hasGoogleCredentials ? "Calendar connected" : "Not Connected"}
                        size="small"
                        sx={{
                          backgroundColor: googleStatus.hasGoogleCredentials ? "#2e7d32" : "#757575",
                          color: "#fff",
                          fontSize: "0.7rem",
                          fontWeight: 600,
                        }}
                      />
                    )}
                  </Box>

                  {googleStatus.hasGoogleCredentials ? (
                    <Box sx={{ 
                      p: 2, 
                      backgroundColor: '#e8f5e9', 
                      borderRadius: '8px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      flexDirection: { xs: 'column', sm: 'row' },
                      gap: { xs: 2, sm: 0 }
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CheckCircle sx={{ color: '#2e7d32' }} />
                        <Typography variant="body2" sx={{ color: '#2e7d32', fontWeight: 500 }}>
                          Google Calendar is connected
                        </Typography>
                      </Box>
                      {/* <Button
                        variant="outlined"
                        color="primary"
                        size="small"
                        onClick={handleConnectGoogle}
                        sx={{
                          textTransform: 'none',
                          fontSize: '0.75rem',
                          padding: '4px 12px',
                          minWidth: 'auto',
                        }}
                      >
                        Reconnect
                      </Button> */}
                    </Box>
                  ) : (
                    <Box sx={{ 
                      p: 2, 
                      backgroundColor: '#fff3e0', 
                      borderRadius: '8px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      flexDirection: { xs: 'column', sm: 'row' },
                      gap: { xs: 2, sm: 0 }
                    }}>
                      <Typography variant="body2" sx={{ color: '#e65100', fontSize: { xs: '0.8rem', sm: '0.85rem' } }}>
                        Google Calendar is not connected
                      </Typography>
                      <Button
                        variant="contained"
                        size="small"
                        onClick={handleConnectGoogle}
                        disabled={googleOAuth.oauthLoading}
                        sx={{
                          backgroundColor: "#219297",
                          textTransform: 'none',
                          fontSize: '0.75rem',
                          padding: '6px 16px',
                          color: "#ffffff",
                          '&:hover': {
                            backgroundColor: "#1a767a",
                            color: "#ffffff",
                          },
                        }}
                      >
                        {googleOAuth.oauthLoading ? "Connecting..." : "Connect Google Calendar"}
                      </Button>
                    </Box>
                  )}
                </Box>
                {googleStatus.hasGoogleCredentials && (
                  <Box>
                    {teamScheduling.isLoading ? (
                      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
                        <CircularProgress sx={{ color: "#219297", mr: 2 }} />
                        <Typography sx={{ color: "#666" }}>Loading team scheduling data...</Typography>
                      </Box>
                    ) : teamScheduling.teamSchedulingStatus ? (
                      <Box>
                        <Box sx={{ 
                          mb: 3, 
                          p: 2.5, 
                          backgroundColor: '#e8f5e9', 
                          borderRadius: '8px',
                          border: '1px solid #c8e6c9'
                        }}>
                          <Typography variant="subtitle2" fontWeight="600" sx={{ color: '#2e7d32', mb: 1 }}>
                            Team Scheduling Configuration
                          </Typography>
                          
                          <Grid container spacing={2}>
                            <Grid item xs={12} md={6}>
                              <Typography variant="body2" sx={{ color: '#424242' }}>
                                <strong>Team Members:</strong> {teamScheduling.teamMembers.length}
                              </Typography>
                            </Grid>
                            <Grid item xs={12} md={6}>
                              <Typography variant="body2" sx={{ color: '#424242' }}>
                                <strong>Slot Duration:</strong> {teamScheduling.schedulingSettings.slot_duration_minutes} minutes
                              </Typography>
                            </Grid>
                            <Grid item xs={12} md={6}>
                              <Typography variant="body2" sx={{ color: '#424242' }}>
                                <strong>Working Hours:</strong> {teamScheduling.schedulingSettings.working_hours_start} - {teamScheduling.schedulingSettings.working_hours_end}
                              </Typography>
                            </Grid>
                            <Grid item xs={12} md={6}>
                              <Typography variant="body2" sx={{ color: '#424242' }}>
                                <strong>Working Days:</strong> {teamScheduling.schedulingSettings.working_days.length} days/week
                              </Typography>
                            </Grid>
                          </Grid>
                        </Box>

                        <Box sx={{ mb: 3 }}>
                          <Box sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'space-between', 
                            mb: 2,
                            flexDirection: { xs: 'column', sm: 'row' },
                            gap: { xs: 2, sm: 0 }
                          }}>
                            <Typography variant="subtitle1" fontWeight="600" sx={{ color: '#219297' }}>
                              Team Members ({teamScheduling.teamMembers.length})
                            </Typography>
                            <Button
                              variant="outlined"
                              size="small"
                              startIcon={<PersonAdd />}
                              onClick={() => setTeamManagementModalOpen(true)}
                              sx={{
                                color: '#219297',
                                borderColor: '#219297',
                                textTransform: 'none',
                                fontSize: '0.8rem',
                              }}
                            >
                              Manage Team
                            </Button>
                          </Box>
                          
                          {teamScheduling.teamMembers.length > 0 ? (
                            <List sx={{ 
                              backgroundColor: '#fff', 
                              borderRadius: '8px',
                              border: '1px solid #e0f0f0'
                            }}>
                              {teamScheduling.teamMembers.slice(0, 5).map((member, index) => (
                                <ListItem key={index} sx={{p:1}}>
                                  <ListItemAvatar>
                                    <Avatar sx={{ backgroundColor: '#219297' }}>
                                      {member.name?.[0]?.toUpperCase() || 'T'}
                                    </Avatar>
                                  </ListItemAvatar>
                                  <ListItemText
                                    primary={
                                      <Box display="flex" alignItems="center" gap={1}>
                                        <Typography variant="body2" fontWeight="500">
                                          {member.name || member.email || member.calendar_id}
                                        </Typography>
                                        <Chip 
                                          label={`Priority: ${member.priority}`}
                                          size="small"
                                          sx={{ 
                                            backgroundColor: '#e0f7fa',
                                            color: '#219297',
                                            fontSize: '0.7rem'
                                          }}
                                        />
                                      </Box>
                                    }
                                    secondary={member.email || member.calendar_id}
                                  />
                                </ListItem>
                              ))}
                              
                              {teamScheduling.teamMembers.length > 5 && (
                                <ListItem>
                                  <Typography variant="body2" sx={{ color: '#666', textAlign: 'center', width: '100%' }}>
                                    ...and {teamScheduling.teamMembers.length - 5} more team members
                                  </Typography>
                                </ListItem>
                              )}
                            </List>
                          ) : (
                            <Alert severity="info" sx={{ borderRadius: '8px' }}>
                              No team members configured yet. Click "Manage Team" to add team members.
                            </Alert>
                          )}
                        </Box>

                        {/* <Box>
                          <Box sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'space-between', 
                            mb: 2,
                            flexDirection: { xs: 'column', sm: 'row' },
                            gap: { xs: 2, sm: 0 }
                          }}>
                            <Typography variant="subtitle1" fontWeight="600" sx={{ color: '#219297' }}>
                              Scheduling Settings
                            </Typography>
                            <Button
                              variant="text"
                              size="small"
                              startIcon={showSettings ? <ExpandLess /> : <ExpandMore />}
                              onClick={() => setShowSettings(!showSettings)}
                              sx={{
                                color: '#219297',
                                textTransform: 'none',
                                fontSize: '0.8rem',
                              }}
                            >
                              {showSettings ? 'Hide' : 'Show'} Settings
                            </Button>
                          </Box>
                          
                          <Collapse in={showSettings}>
                            <Box sx={{ 
                              backgroundColor: '#fff', 
                              borderRadius: '8px', 
                              p: 2.5,
                              border: '1px solid #e0f0f0',
                              mb: 2
                            }}>
                              <Grid container spacing={2}>
                                <Grid item xs={12} md={6}>
                                  <CustomInput
                                    type="number"
                                    value={teamScheduling.schedulingSettings.slot_duration_minutes}
                                    onChange={(e) => teamScheduling.setSchedulingSettings({
                                      ...teamScheduling.schedulingSettings,
                                      slot_duration_minutes: parseInt(e.target.value) || 30
                                    })}
                                    placeholder="Slot Duration (minutes)"
                                    helperText="Default appointment length"
                                    startIcon={<AccessTime />}
                                  />
                                </Grid>
                                
                                <Grid item xs={12} md={6}>
                                  <Typography variant="caption" display="block" gutterBottom>
                                    Working Hours
                                  </Typography>
                                  <Box display="flex" alignItems="center" gap={1}>
                                    <CustomInput
                                      type="time"
                                      value={teamScheduling.schedulingSettings.working_hours_start}
                                      onChange={(e) => teamScheduling.setSchedulingSettings({
                                        ...teamScheduling.schedulingSettings,
                                        working_hours_start: e.target.value
                                      })}
                                      className="working-hours-input"
                                      fullWidth={false}
                                      style={{ flex: 1 }}
                                    />
                                    <Typography variant="body2">to</Typography>
                                    <CustomInput
                                      type="time"
                                      value={teamScheduling.schedulingSettings.working_hours_end}
                                      onChange={(e) => teamScheduling.setSchedulingSettings({
                                        ...teamScheduling.schedulingSettings,
                                        working_hours_end: e.target.value
                                      })}
                                      className="working-hours-input"
                                      fullWidth={false}
                                      style={{ flex: 1 }}
                                    />
                                  </Box>
                                </Grid>
                              </Grid>
                              
                              <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                                <Button
                                  variant="contained"
                                  size="small"
                                  onClick={handleSaveSettings}
                                  disabled={savingSettings}
                                  sx={{
                                    backgroundColor: '#219297',
                                    textTransform: 'none',
                                  }}
                                >
                                  {savingSettings ? 'Saving...' : 'Save Settings'}
                                </Button>
                              </Box>
                            </Box>
                          </Collapse>
                        </Box> */}

                        {!isTeamSchedulingActive && (
                          <Box sx={{ mt: 3, textAlign: 'center' }}>
                            <Button
                              variant="contained"
                              size="large"
                              onClick={handleEnableTeamScheduling}
                              disabled={isSavingTeamProvider || teamScheduling.teamMembers.length === 0}
                              sx={{
                                backgroundColor: '#219297',
                                textTransform: 'none',
                                padding: '12px 32px',
                                fontSize: '1rem',
                                color: '#ffffff',
                                '&:hover': {
                                  backgroundColor: '#1a767a',
                                  color: '#ffffff',
                                },
                              }}
                            >
                              {isSavingTeamProvider ? 'Enabling...' : 'Enable Team Scheduling'}
                            </Button>
                            {teamScheduling.teamMembers.length === 0 && (
                              <Typography variant="body2" sx={{ color: '#666', mt: 1 }}>
                                Add at least one team member to enable team scheduling.
                              </Typography>
                            )}
                          </Box>
                        )}

                        {isTeamSchedulingActive && teamScheduling.teamSchedulingStatus?.booking_url && (
                          <Box sx={{ 
                            mt: 3, 
                            p: 2.5, 
                            backgroundColor: '#e3f2fd', 
                            borderRadius: '8px',
                            border: '1px solid #bbdefb'
                          }}>
                            <Typography variant="subtitle2" fontWeight="600" sx={{ color: '#1565c0', mb: 1 }}>
                              Public Booking URL
                            </Typography>
                            <CustomInput
                              value={teamScheduling.teamSchedulingStatus.booking_url}
                              onChange={() => {}}
                              placeholder="Booking URL"
                              startIcon={<Link />}
                              endAdornment={
                                <Tooltip title="Copy URL">
                                  <IconButton
                                    size="small"
                                    onClick={() => {
                                      navigator.clipboard.writeText(teamScheduling.teamSchedulingStatus.booking_url);
                                      setSnackbar({
                                        open: true,
                                        severity: "success",
                                        message: "Booking URL copied to clipboard!",
                                      });
                                    }}
                                    sx={{ p: 0.5 }}
                                  >
                                    <ContentCopy fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              }
                              disabled
                            />
                            <Typography variant="caption" sx={{ color: '#1565c0', mt: 1, display: 'block' }}>
                              Share this URL with clients to book appointments with your team.
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    ) : (
                      <Box sx={{ textAlign: 'center', py: 4 }}>
                        <Avatar
                          sx={{
                            width: 64,
                            height: 64,
                            backgroundColor: '#e0f7fa',
                            color: '#219297',
                            margin: '0 auto 16px',
                          }}
                        >
                          <Groups sx={{ fontSize: 32 }} />
                        </Avatar>
                        <Typography variant="h6" sx={{ color: '#219297', mb: 1, fontSize: { xs: '1.1rem', md: '1.25rem' } }}>
                          Team Scheduling Not Configured
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#666', mb: 3, fontSize: { xs: '0.85rem', md: '0.9rem' } }}>
                          Set up team scheduling to enable round-robin booking across multiple team members.
                        </Typography>
                        <Button
                          variant="contained"
                          startIcon={<Settings />}
                          onClick={handleEnableTeamScheduling}
                          sx={{
                            backgroundColor: '#219297',
                            textTransform: 'none',
                            padding: '10px 24px',
                            fontSize: { xs: '0.9rem', md: '1rem' },
                          }}
                        >
                          Configure Team Scheduling
                        </Button>
                      </Box>
                    )}
                  </Box>
                )}
              </Paper>
            </Grid>

            <Grid item xs={12} md={4}>
              <Paper sx={{ 
                p: { xs: 2, md: 2.5 }, 
                borderRadius: '12px', 
                backgroundColor: '#f8fafa', 
                height: '100%',
                border: '1px solid #e0f0f0'
              }}>
                <Typography 
                  variant="subtitle1" 
                  sx={{ 
                    mb: 2, 
                    color: '#219297',
                    fontWeight: 600,
                    fontSize: { xs: '0.9rem', md: '0.95rem' },
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1
                  }}
                >
                  <InfoOutlined fontSize="small" />
                  How Team Scheduling Works
                </Typography>
                
                <Box sx={{ 
                  backgroundColor: '#fff', 
                  borderRadius: '8px', 
                  p: 2.5,
                  border: '1px solid #e0f0f0'
                }}>
                  {providerInstructions["team-scheduling"].map((step, index) => (
                    <InstructionStep
                      key={index}
                      number={index + 1}
                      title={step.title}
                    >
                      {step.content}
                    </InstructionStep>
                  ))}
                  
                  <Alert severity="info" sx={{ mt: 2, borderRadius: '6px' }}>
                    <Typography variant="caption" sx={{ color: '#1565c0' }}>
                      Team scheduling requires team members to share their Google Calendars with your account.
                    </Typography>
                  </Alert>
                </Box>

                {providerHasVideo("team-scheduling") && (
                  <Box sx={{ mt: 3 }}>
                    <Typography 
                      variant="subtitle1" 
                      sx={{ 
                        mb: 1.5, 
                        color: '#219297',
                        fontWeight: 600,
                        fontSize: { xs: '0.9rem', md: '0.95rem' }
                      }}
                    >
                      Video Guide
                    </Typography>
                    <Button
                      fullWidth
                      startIcon={<PlayCircleOutline />}
                      onClick={() => {
                        const embedUrl = getEmbedUrlForProvider("team-scheduling");
                        if (embedUrl) {
                          setCurrentVideoUrl(embedUrl);
                          setShowVideo(true);
                        }
                      }}
                      sx={{
                        color: '#219297',
                        border: '1px solid #219297',
                        textTransform: 'none',
                        borderRadius: '8px',
                        padding: '10px',
                        fontSize: '0.9rem',
                        '&:hover': {
                          backgroundColor: '#e0f7fa',
                          borderColor: '#1a767a',
                        },
                      }}
                    >
                      Watch Team Scheduling Setup Guide
                    </Button>
                  </Box>
                )}
              </Paper>
            </Grid>
          </Grid>
        )}

        <TeamManagementModal
          open={teamManagementModalOpen}
          onClose={() => setTeamManagementModalOpen(false)}
          domainId={domainId}
          refreshData={teamScheduling.fetchTeamSchedulingStatus}
          initialTeamData={teamScheduling.teamMembers}
        />
      </SoftBox>
    </DashboardLayout>
  );
};

export default Integrations;